import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Database, Settings, Trash2, Plus } from 'lucide-react';
import {
  listFieldDefinitions,
  createFieldDefinition,
  updateFieldDefinition,
  deleteFieldDefinition,
  patchEntityMetadata,
  type FieldDefinition,
} from '../packages/api-client/customfields-api';

interface Props {
  entityId: string;
  entityTypeId: string;
  /** The current metadata object from the entity */
  metadata: Record<string, unknown>;
  /** Called after a successful metadata patch so the parent can update its state */
  onMetadataChange?: (updated: Record<string, unknown>) => void;
}

const FIELD_TYPES: FieldDefinition['field_type'][] = [
  'text', 'number', 'date', 'boolean', 'select', 'url',
];

export function EntityCustomFields({ entityId, entityTypeId, metadata, onMetadataChange }: Props) {
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [localMeta, setLocalMeta] = useState<Record<string, unknown>>(metadata ?? {});
  const [showManage, setShowManage] = useState(false);
  const [newField, setNewField] = useState<Partial<FieldDefinition>>({ name: '', field_type: 'text', options: [], required: false });
  const [adding, setAdding] = useState(false);
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    listFieldDefinitions(entityTypeId).then(setFields).catch(() => {});
  }, [entityTypeId]);

  useEffect(() => {
    setLocalMeta(metadata ?? {});
  }, [metadata]);

  // ── Autosave: debounce 600ms then PATCH ──────────────────────────────────
  const handleChange = useCallback((key: string, value: unknown) => {
    const next = { ...localMeta, [key]: value };
    setLocalMeta(next);
    if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(async () => {
      try {
        await patchEntityMetadata(entityId, { [key]: value });
        onMetadataChange?.(next);
      } catch { /* silent */ }
    }, 600);
  }, [entityId, localMeta, onMetadataChange]);

  // ── Add field definition ─────────────────────────────────────────────────
  async function handleAddField(e: React.FormEvent) {
    e.preventDefault();
    if (!newField.name?.trim()) return;
    setAdding(true);
    try {
      const f = await createFieldDefinition(entityTypeId, newField);
      setFields(prev => [...prev, f]);
      setNewField({ name: '', field_type: 'text', options: [], required: false });
    } finally { setAdding(false); }
  }

  async function handleDeleteField(f: FieldDefinition) {
    if (!confirm(`Delete field "${f.name}"? Values won't be removed from existing records.`)) return;
    await deleteFieldDefinition(entityTypeId, f.id);
    setFields(prev => prev.filter(d => d.id !== f.id));
  }

  function renderInput(f: FieldDefinition) {
    const val = localMeta[f.key];
    const common = { id: `cf-${f.key}`, className: 'custom-field-input' };

    switch (f.field_type) {
      case 'boolean':
        return (
          <input type="checkbox" {...common} style={{ width: '1rem', height: '1rem', accentColor: 'var(--color-primary)' }}
            checked={!!val} onChange={e => handleChange(f.key, e.target.checked)} />
        );
      case 'number':
        return <input type="number" {...common} value={val as number ?? ''} onChange={e => handleChange(f.key, e.target.value === '' ? null : Number(e.target.value))} />;
      case 'date':
        return <input type="date" {...common} value={val as string ?? ''} onChange={e => handleChange(f.key, e.target.value)} />;
      case 'url':
        return <input type="url" {...common} placeholder="https://" value={val as string ?? ''} onChange={e => handleChange(f.key, e.target.value)} />;
      case 'select':
        return (
          <select {...common} value={val as string ?? ''} onChange={e => handleChange(f.key, e.target.value)}>
            <option value="">— Select —</option>
            {(f.options ?? []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      default: // text
        return <input type="text" {...common} value={val as string ?? ''} placeholder="—" onChange={e => handleChange(f.key, e.target.value)} />;
    }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontWeight: 700 }}><Database size={16} aria-hidden="true" /> Custom Fields</span>
        <button className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }} onClick={() => setShowManage(v => !v)}>
          {showManage ? 'Done' : <><Settings size={14} aria-hidden="true" /> Manage Fields</>}
        </button>
      </div>

      {/* Field values */}
      {!showManage && (
        fields.length === 0
          ? <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: '1rem 0' }}>
              No custom fields yet. Click <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 2, verticalAlign: 'middle' }}><Settings size={12} aria-hidden="true" /> Manage Fields</strong> to add some.
            </p>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {fields.map(f => (
                <div key={f.id} className="custom-field-row">
                  <label className="custom-field-label" htmlFor={`cf-${f.key}`}>
                    {f.name}{f.required && <span style={{ color: 'var(--color-danger)', marginLeft: 2 }}>*</span>}
                  </label>
                  {renderInput(f)}
                </div>
              ))}
            </div>
      )}

      {/* Manage fields panel */}
      {showManage && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {/* Existing fields */}
          {fields.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-border)' }}>
              <span style={{ flex: 1, fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{f.name}</span>
              <span className="badge badge-neutral" style={{ fontSize: '0.65rem' }}>{f.field_type}</span>
              {f.required && <span className="badge badge-warning" style={{ fontSize: '0.65rem' }}>required</span>}
              <button className="btn btn-ghost btn-sm" aria-label={`Delete field ${f.name}`} style={{ color: 'var(--color-danger)' }} onClick={() => handleDeleteField(f)}><Trash2 size={14} aria-hidden="true" /></button>
            </div>
          ))}

          {/* Add new field form */}
          <form onSubmit={handleAddField} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
              <div className="form-group">
                <label className="form-label">Field Name *</label>
                <input className="form-input" required placeholder="e.g. Contract Value"
                  value={newField.name ?? ''} onChange={e => setNewField(f => ({ ...f, name: e.target.value, key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-input" value={newField.field_type ?? 'text'} onChange={e => setNewField(f => ({ ...f, field_type: e.target.value as FieldDefinition['field_type'] }))}>
                  {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            {newField.field_type === 'select' && (
              <div className="form-group">
                <label className="form-label">Options (comma-separated)</label>
                <input className="form-input" placeholder="Option A, Option B, Option C"
                  onChange={e => setNewField(f => ({ ...f, options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} />
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!newField.required} onChange={e => setNewField(f => ({ ...f, required: e.target.checked }))} />
                Required
              </label>
              <button className="btn btn-primary btn-sm" type="submit" disabled={adding} style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                {adding ? '…' : <><Plus size={14} aria-hidden="true" /> Add Field</>}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
